import { useContext, useState } from "react";
import Button from "../../../atoms/button"
import Loading from "../../../atoms/loading"
import ToastContext from "../../../../context/toastProvider";
import { addProduct } from "../../functions";
import { cashnator, cashnatorFloat, cpfMask, generateRandomID, phoneMask } from "../../../utils/functions";
import { getGradingTypeLabel, getPreLacreFormatLabel, normalizeGradingType, priorityTypes } from "../../../utils/gradingTypes";
import { statusObject } from "../../../utils/allProductStatus";

const DEFAULT_STATUS = "PENDING";

const Row = ({ label, value }) => (
    value === undefined || value === null || value === "" ? null : (
        <div className="flex justify-between gap-4 border-b border-primary/10 py-1 text-sm last:border-b-0">
            <span className="text-laranjinha/50">{label}</span>
            <span className="text-right text-laranjinha/90">{value}</span>
        </div>
    )
);

const Card = ({ title, onEdit, children }) => (
    <section className="rounded-xl border border-primary/25 bg-mgs-surface/60 px-4 py-3 text-left">
        <div className="mb-1 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-laranjinha/80">{title}</h3>
            {onEdit ? (
                <button type="button" onClick={onEdit} className="text-xs text-laranjinha/60 underline hover:text-primary">
                    Editar
                </button>
            ) : null}
        </div>
        {children}
    </section>
);

const SummaryStep = ({ user, productData, setProductData, deliveryData, setStep, userData, refresh, stores }) => {

    const { goodToast, badToast } = useContext(ToastContext);

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(productData?.status || DEFAULT_STATUS);
    const [showStatus, setShowStatus] = useState(Boolean(productData?.status && productData.status !== DEFAULT_STATUS));

    const goBack = () => {
        setStep(4);
    }

    const handleStatus = (value) => {
        setStatus(value);
        setProductData(pd => ({ ...pd, status: value }));
    }

    const handleSubmit = async () => {
        setLoading(true);
        const body = {
            localId: generateRandomID(),
            userId: userData?._id,
            gradingType: productData?.gradingType,
            autographEvaluation: !!productData?.autographEvaluation,
            ...(normalizeGradingType(productData?.gradingType) === "pre" && productData?.preLacreFormat ? { preLacreFormat: productData?.preLacreFormat } : {}),
            numberOfCards: productData?.numberOfCards,
            delivery: deliveryData?.delivery,
            priority: productData?.priority,
            cardsValue: productData?.declaredValue,
            insurance: productData?.insurance,
            store: deliveryData?.store,
            shipping: deliveryData?.shipping?.price,
            shippingService: deliveryData?.shipping?.service,
            street: deliveryData?.address?.street,
            houseNumber: deliveryData?.address?.houseNumber,
            district: deliveryData?.address?.district,
            city: deliveryData?.address?.city,
            country: deliveryData?.address?.country,
            complement: deliveryData?.address?.complement,
            postalCode: deliveryData?.address?.postalCode,
            state: deliveryData?.address?.state,
            finalPrice: productData?.finalPrice,
            deadline: productData?.deadline,
            status,
            comments: [{ date: new Date(), text: `Criado pelo admin por ${user?.email}` }, ...(productData?.comments ?? [])],
            createdByAdmin: true,
        }
        const data = await addProduct(user?.token, body, false);
        if (data?.type !== "success") {
            if (data?.message === 'Deadline exceeds limit') {
                badToast('Erro ao criar pedido: Prazo excede o limite permitido');
                setLoading(false);
                return null;
            }
            badToast(`Erro na criação de pedido${data?.message ? `: ${data?.message}` : ""}`);
            setLoading(false);
            return null;
        }
        goodToast("Pedido criado com sucesso!");
        setLoading(false);
        refresh(data?.data);
    }

    const storeName = stores?.reduce((result, s) => (
        s?.id === deliveryData?.store || s?._id === deliveryData?.store
            ? `${s?.name}${s?.city ? ` — ${s.city}/${s.state}` : ""}`
            : result
    ), null);

    return (
        <div className="relative text-left">
            <section className="mb-4 rounded-xl border border-primary bg-mgs-surface p-4 shadow-lg">
                <h2 className="mb-3 border-b border-primary/30 pb-2 text-lg font-semibold text-laranjinha">Revisão do pedido</h2>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Card title="Cliente" onEdit={() => setStep(0)}>
                        <Row label="Nome" value={userData?.name} />
                        <Row label="E-mail" value={userData?.email} />
                        <Row label="Telefone" value={phoneMask(userData?.phone)} />
                        <Row
                            label={userData?.documentType === "other" ? "Documento" : userData?.documentType}
                            value={userData?.documentType === "other" ? userData?.documentNumber : cpfMask(userData?.documentNumber)}
                        />
                    </Card>

                    <Card title="Entrega" onEdit={() => setStep(1)}>
                        {
                            deliveryData?.delivery === "presencial" ? (
                                <>
                                    <Row label="Tipo" value="Presencial (retirada em loja)" />
                                    <Row label="Loja" value={storeName} />
                                </>
                            ) : (
                                <>
                                    <Row label="Tipo" value="Correios" />
                                    <Row label="Recebedor" value={deliveryData?.address?.name} />
                                    <Row label="Endereço" value={`${deliveryData?.address?.street ?? ""}, ${deliveryData?.address?.houseNumber ?? ""}`} />
                                    <Row label="Cidade" value={`${deliveryData?.address?.city ?? ""}/${deliveryData?.address?.state ?? ""}`} />
                                    <Row label="CEP" value={deliveryData?.address?.postalCode} />
                                    <Row label="Complemento" value={deliveryData?.address?.complement} />
                                </>
                            )
                        }
                    </Card>

                    <Card title="Pedido" onEdit={() => setStep(2)}>
                        <Row label="Tipo" value={getGradingTypeLabel(productData?.gradingType)} />
                        <Row label="Nº de itens" value={productData?.numberOfCards} />
                        <Row label="Autógrafo" value={productData?.autographEvaluation ? "Sim" : "Não"} />
                        {
                            normalizeGradingType(productData?.gradingType) === "pre" && productData?.preLacreFormat ?
                                <Row label="Pré lacre" value={getPreLacreFormatLabel(productData?.preLacreFormat)} />
                                : null
                        }
                        <Row label="Prioridade" value={priorityTypes[productData?.priority]} />
                    </Card>

                    <Card title="Valores e prazo" onEdit={() => setStep(4)}>
                        <Row label="Valor declarado" value={cashnator(productData?.declaredValue)} />
                        <Row label="Taxa de manuseio" value={cashnator(productData?.insurance)} />
                        {
                            deliveryData?.delivery === "correios" ?
                                <Row label={`Frete (${deliveryData?.shipping?.service ?? "—"})`} value={cashnatorFloat(deliveryData?.shipping?.price)} />
                                : null
                        }
                        <Row label="Prazo" value={productData?.deadline ? `${productData.deadline} dias úteis` : null} />
                    </Card>
                </div>

                <div className="mt-3 flex items-center justify-between rounded-lg border border-primary bg-mgs-elevated px-4 py-3">
                    <span className="text-sm text-laranjinha/70">Valor final do pedido</span>
                    <span className="text-2xl font-bold text-laranjinha">{cashnator(productData?.finalPrice)}</span>
                </div>

                {
                    productData?.comments?.length ? (
                        <div className="mt-3 rounded-lg border border-primary/15 bg-mgs-bg/40 px-4 py-2">
                            <p className="mb-1 text-xs font-semibold text-laranjinha/60">Anotações ({productData.comments.length})</p>
                            {productData.comments.map((c, i) => (
                                <p key={i} className="text-sm text-laranjinha/80">• {c?.text}</p>
                            ))}
                        </div>
                    ) : null
                }

                {/* Fluxo alternativo: por padrão o pedido nasce como PAGAMENTO PENDENTE. */}
                <div className="mt-3 text-sm">
                    {
                        showStatus ? (
                            <label className="flex flex-wrap items-center gap-2 text-laranjinha/70">
                                Status inicial:
                                <select className="rounded border border-primary/40 bg-mgs-surface px-2 py-1 text-laranjinha" value={status} onChange={e => handleStatus(e.target.value)}>
                                    {
                                        Object.keys(statusObject).map((o, i) => (
                                            <option value={o} key={i}>{statusObject[o]}</option>
                                        ))
                                    }
                                </select>
                                {
                                    status !== DEFAULT_STATUS ? (
                                        <button type="button" className="text-xs text-laranjinha/50 underline hover:text-primary" onClick={() => handleStatus(DEFAULT_STATUS)}>
                                            usar padrão
                                        </button>
                                    ) : null
                                }
                            </label>
                        ) : (
                            <p className="text-laranjinha/50">
                                O pedido será criado com status <span className="text-laranjinha/80">{statusObject[status]}</span>.
                                {" "}
                                <button type="button" className="text-laranjinha/60 underline hover:text-primary" onClick={() => setShowStatus(true)}>
                                    Alterar status inicial
                                </button>
                            </p>
                        )
                    }
                </div>
            </section>

            <div className="m-auto flex w-fit gap-3">
                <Button click={goBack} classes="bg-purple-400 hover:bg-purple-300 px-4">← Voltar</Button>
                <Button click={handleSubmit} classes="bg-green-400 hover:bg-green-300 px-8 py-1 font-semibold">Criar pedido ✓</Button>
            </div>
            {/* LOADING OVERLAY */}
            {
                loading ?
                    <div className="absolute top-0 left-0 w-full h-full bg-[#000000dd] flex justify-center items-center">
                        <Loading />
                    </div>
                    : null
            }
        </div>
    )
}

export default SummaryStep;
